/**
 * תזכורות — the message library, and what was actually sent.
 *
 * The send log is the point of this screen. "Did the client get the reminder?" is a
 * question that comes up when a penalty is disputed, so the log shows the exact rendered
 * text, the delivery state and whether it was read — and the text is stored per message so
 * a later edit to a template cannot rewrite history.
 *
 * Locally nothing is actually delivered: every channel writes to a filesystem outbox that
 * `/__dev/outbox` reads. That is deliberate, and the screen says so rather than implying
 * a WhatsApp message reached anyone.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@onecpa/shared";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  KpiStrip,
  PageHeader,
  Panel,
  TabStrip,
  Textarea,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { useRole, useSession } from "../app/session.js";
import { ReminderRules } from "./ReminderRules.js";

const CHANNEL_LABELS: Record<string, string> = {
  in_app: "באפליקציה",
  email: "דוא״ל",
  whatsapp: "וואטסאפ",
  sms: "SMS",
};

const DELIVERY_LABELS: Record<
  string,
  { label: string; tone: "neutral" | "info" | "success" | "danger" }
> = {
  queued: { label: "בהמתנה", tone: "neutral" },
  sent: { label: "נשלח", tone: "info" },
  delivered: { label: "נמסר", tone: "info" },
  read: { label: "נקרא", tone: "success" },
  failed: { label: "נכשל", tone: "danger" },
};

type TabId = "rules" | "log" | "queue" | "templates";

export function Reminders() {
  const session = useSession();
  const { isManager } = useRole();
  // The rules first: what the system will do next matters more than what it did.
  const [tab, setTab] = useState<TabId>("rules");

  const messages = useQuery({
    queryKey: ["messages", session.orgId],
    queryFn: () => api.settings.messages(session.orgId),
  });

  const templates = useQuery({
    queryKey: ["templates", session.orgId],
    queryFn: () => api.settings.templates(session.orgId),
    enabled: isManager && tab === "templates",
  });

  // Fetched regardless of the active tab, because the count is on the tab itself.
  const rules = useQuery({
    queryKey: ["reminder-rules", session.orgId],
    queryFn: () => api.settings.reminderRules(session.orgId),
  });

  if (messages.error) {
    return (
      <ErrorState
        message={(messages.error as Error).message}
        onRetry={() => void messages.refetch()}
      />
    );
  }

  const sent = messages.data?.sent ?? [];
  const queued = messages.data?.queued ?? [];

  return (
    <>
      <PageHeader title="תזכורות" subtitle="מה נשלח, מה ממתין, ומה כתוב בתבניות">
        <TabStrip
          tabs={[
            { id: "rules", label: "כללים", count: rules.data?.rules.length },
            { id: "log", label: "נשלחו", count: sent.length },
            { id: "queue", label: "בהמתנה", count: queued.length },
            ...(isManager ? [{ id: "templates", label: "תבניות" }] : []),
          ]}
          active={tab}
          onSelect={(id) => setTab(id as TabId)}
        />
      </PageHeader>

      <KpiStrip
        loading={messages.isLoading}
        items={[
          { label: "נשלחו", value: sent.length },
          { label: "נקראו", value: sent.filter((row) => row.readAt).length, tone: "ledger" },
          { label: "בהמתנה", value: queued.length, tone: queued.length > 0 ? "pending" : "ink" },
          {
            label: "נכשלו",
            value: queued.filter((row) => row.lastError).length,
            tone: queued.some((row) => row.lastError) ? "red" : "ink",
          },
          { label: "ערוצים", value: "דוא״ל · אפליקציה" },
        ]}
      />

      <div className="mx-5 mt-4 flex items-start gap-2 rounded-card border border-info-ink/30 bg-info-bg px-3.5 py-2.5">
        <span className="text-xs text-info-ink">
          בסביבה המקומית ההודעות נכתבות לתיקיית dev-data/outbox ואינן נשלחות לאף אחד. ניתן לקרוא
          אותן גם בכתובת <bdi dir="ltr">/__dev/outbox</bdi> של ה-API.
        </span>
      </div>

      <div className="p-5">
        {tab === "rules" && <ReminderRules />}

        {tab === "log" && (
          <Panel title="היסטוריית שליחה">
            {sent.length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {sent.map((row) => {
                  const delivery = DELIVERY_LABELS[row.deliveryStatus] ?? {
                    label: row.deliveryStatus,
                    tone: "neutral" as const,
                  };
                  return (
                    <li key={row.id} className="px-3.5 py-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-ink">
                          {row.clientName ?? "המשרד"}
                        </span>
                        <Badge tone="neutral">{CHANNEL_LABELS[row.channel] ?? row.channel}</Badge>
                        <Badge tone={delivery.tone}>{delivery.label}</Badge>
                        <bdi className="tnum ms-auto text-2xs text-ink-faint">
                          {formatDateTime(row.sentAt)}
                        </bdi>
                      </div>
                      {row.renderedBody && (
                        // The exact text sent, preserved with its line breaks: this is the
                        // evidence, not a summary of it.
                        <p className="mt-1.5 border-s-2 border-rule ps-2.5 text-xs whitespace-pre-line text-ink-muted">
                          {row.renderedBody}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState title="לא נשלחו הודעות" icon="bell" />
            )}
          </Panel>
        )}

        {tab === "queue" && (
          <Panel title="ממתינות לשליחה">
            {queued.length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {queued.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center gap-2 px-3.5 py-2.5">
                    <span className="text-xs font-semibold text-ink">
                      {row.clientName ?? "המשרד"}
                    </span>
                    <span className="text-2xs text-ink-faint">{row.templateKey}</span>
                    <Badge tone="neutral">{CHANNEL_LABELS[row.channel] ?? row.channel}</Badge>
                    {row.attempts > 0 && <Badge tone="warning">{row.attempts} נסיונות</Badge>}
                    {row.lastError && <Badge tone="danger">{row.lastError}</Badge>}
                    <bdi className="tnum ms-auto text-2xs text-ink-faint">
                      {formatDateTime(row.scheduledFor)}
                    </bdi>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="אין הודעות בהמתנה"
                description="העובד ברקע מרוקן את התור כל 15 שניות בסביבה המקומית."
                icon="check"
              />
            )}
          </Panel>
        )}

        {tab === "templates" && isManager && (
          <div className="flex flex-col gap-4">
            {(templates.data?.templates ?? []).map((template) => (
              <TemplateEditor key={template.id} template={template} />
            ))}
            {templates.data?.templates.length === 0 && (
              <Panel>
                <EmptyState title="אין תבניות" />
              </Panel>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function TemplateEditor({
  template,
}: {
  template: {
    id: string;
    key: string;
    channel: string;
    subject: string | null;
    body: string;
    description: string | null;
    isModified: boolean;
  };
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState(template.subject ?? "");
  const [body, setBody] = useState(template.body);

  const dirty = subject !== (template.subject ?? "") || body !== template.body;

  const save = useMutation({
    mutationFn: () => api.settings.updateTemplate(session.orgId, template.id, { subject, body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      session.toast("התבנית נשמרה");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const restore = useMutation({
    mutationFn: () => api.settings.restoreTemplate(session.orgId, template.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      session.toast("התבנית שוחזרה לברירת המחדל");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  return (
    <Panel
      title={template.description ?? template.key}
      action={
        <span className="flex items-center gap-2">
          {template.isModified && <Badge tone="info">נערכה</Badge>}
          <Badge tone="neutral">{CHANNEL_LABELS[template.channel] ?? template.channel}</Badge>
        </span>
      }
    >
      <div className="flex flex-col gap-3 px-3.5 py-3">
        <Field label="נושא">
          {({ id }) => (
            <Input id={id} value={subject} onChange={(event) => setSubject(event.target.value)} />
          )}
        </Field>
        <Field
          label="גוף ההודעה"
          help="שדות בסוגריים מסולסלים מוחלפים בזמן השליחה, לדוגמה {{clientName}}"
        >
          {({ id }) => (
            <Textarea
              id={id}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className="min-h-28"
            />
          )}
        </Field>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            שמירה
          </Button>
          {/* Always available: the default lives in code, so there is always something
              to restore to no matter how the copy was edited. */}
          <Button variant="ghost" loading={restore.isPending} onClick={() => restore.mutate()}>
            שחזור לברירת מחדל
          </Button>
          <span className="ms-auto text-2xs text-ink-faint" dir="ltr">
            {template.key}
          </span>
        </div>
      </div>
    </Panel>
  );
}
